import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import {
  WA_ASSIGN, WA_CLAIM, WA_SET_STATUS, WA_SET_TAGS, WA_RENAME, WA_UNLINK, WA_AUDIT_LOG, WA_SET_OPT_IN,
} from "../../../Queries/WhatsApp";
import { openTab } from "../../../Slices/tabSlice";
import { upsertWaConversation, fetchWaConversation, fetchWaStatus } from "../../../Slices/whatsappSlice";
import ConsentForm from "./ConsentForm";
import { WA_STATUS_LABELS, displayNameOf, fullTime, gqlErrorMessage, initialsOf, windowRemaining } from "./waUtils";

const MAX_TAGS = 10;

const ContextPanel = ({ detail, onLink, onClose, now }) => {
  const dispatch = useDispatch();
  const { assignees, status } = useSelector((s) => s.whatsapp);
  const conversation = detail.conversation;
  const perms = detail.permissions || {};
  const linked = detail.linked || {};
  const consent = detail.consent || {};
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    setError(null);
    setRenaming(false);
    setAudit(null);
    setShowAudit(false);
    setShowConsent(false);
  }, [conversation.id]);

  const run = async (key, mutation, variables, pick) => {
    setBusy(key);
    setError(null);
    try {
      const { data } = await client.mutate({ mutation, variables });
      const updated = pick(data);
      if (updated) dispatch(upsertWaConversation(updated));
      dispatch(fetchWaStatus());
      return updated;
    } catch (err) {
      setError(gqlErrorMessage(err));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const remaining = windowRemaining(conversation.windowExpiresAt, now);
  const isMine = conversation.assignedTo && conversation.assignedTo === status?.userId;
  const name = displayNameOf(conversation);

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t || (conversation.tags || []).includes(t)) return setTagInput("");
    await run("tags", WA_SET_TAGS, { id: conversation.id, tags: [...(conversation.tags || []), t].slice(0, MAX_TAGS) }, (d) => d.whatsappSetTags);
    setTagInput("");
  };
  const removeTag = (t) => run("tags", WA_SET_TAGS, { id: conversation.id, tags: (conversation.tags || []).filter((x) => x !== t) }, (d) => d.whatsappSetTags);

  const saveName = async () => {
    const ok = await run("rename", WA_RENAME, { id: conversation.id, name: nameInput }, (d) => d.whatsappRenameContact);
    if (ok) setRenaming(false);
  };

  const loadAudit = async () => {
    setShowAudit((v) => !v);
    if (audit) return;
    try {
      const { data } = await client.query({ query: WA_AUDIT_LOG, variables: { conversationId: conversation.id, limit: 30 }, fetchPolicy: "network-only" });
      setAudit(data.whatsappAuditLog || []);
    } catch (err) {
      setError(gqlErrorMessage(err));
    }
  };

  const optOut = async () => {
    setBusy("consent");
    try {
      await client.mutate({ mutation: WA_SET_OPT_IN, variables: { phone: conversation.waId, optIn: false, note: "Marked opted-out by staff", leadId: conversation.leadId } });
      dispatch(fetchWaConversation(conversation.id));
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <aside className="wa-context" aria-label="Conversation details">
      <div className="wa-context__hero">
        {onClose && (
          <button type="button" className="wa-icon-btn" style={{ position: "absolute", right: 8, top: 8 }} onClick={onClose} aria-label="Hide details">
            <i className="fa-solid fa-xmark" />
          </button>
        )}
        <div className="wa-avatar wa-avatar--lg">{initialsOf(name, "#")}</div>
        {renaming ? (
          <div style={{ display: "flex", gap: 6 }}>
            <input className="wa-input" value={nameInput} autoFocus maxLength={120} onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { e.stopPropagation(); setRenaming(false); } }} />
            <button type="button" className="wa-btn wa-btn--sm wa-btn--primary" disabled={busy === "rename"} onClick={saveName}>Save</button>
          </div>
        ) : (
          <div className="wa-context__name">
            {name}
            {perms.canAct && (
              <button type="button" className="wa-icon-btn" style={{ width: 24, height: 24 }} onClick={() => { setNameInput(conversation.contactName || ""); setRenaming(true); }} aria-label="Rename contact" title="Rename">
                <i className="fa-solid fa-pen" style={{ fontSize: 11 }} />
              </button>
            )}
          </div>
        )}
        <div className="wa-context__num">{conversation.displayNumber}</div>
        {conversation.profileName && conversation.profileName !== conversation.contactName && (
          <div className="wa-help">WhatsApp profile: {conversation.profileName}</div>
        )}
      </div>

      {error && <div className="wa-alert wa-alert--danger" style={{ margin: "10px 16px 0" }} role="alert">{error}</div>}

      <div className="wa-section">
        <div className="wa-section__title">Reply window</div>
        {remaining ? (
          <div className="wa-window wa-window--open">
            <span className="wa-dot wa-dot--open" />
            <div><b>Free-reply window closes in {remaining}</b>Any message can be sent until then.</div>
          </div>
        ) : (
          <div className="wa-window wa-window--closed">
            <i className="fa-regular fa-clock" />
            <div><b>Window closed — templates only</b>{conversation.lastInboundAt ? `Last message from contact ${fullTime(conversation.lastInboundAt)}.` : "This contact hasn't messaged yet."}</div>
          </div>
        )}
      </div>

      <div className="wa-section">
        <div className="wa-section__title">Assignment & status</div>
        <div className="wa-field">
          <label className="wa-label" htmlFor="wa-assignee">Assigned to</label>
          {perms.canManage ? (
            <select
              id="wa-assignee"
              className="wa-select"
              value={conversation.assignedTo || ""}
              disabled={busy === "assign"}
              onChange={(e) => run("assign", WA_ASSIGN, { id: conversation.id, userId: e.target.value || null }, (d) => d.whatsappAssignConversation)}
            >
              <option value="">Unassigned</option>
              {conversation.assignedTo && !assignees.some((a) => a.id === conversation.assignedTo) && (
                <option value={conversation.assignedTo}>{conversation.assignedToName}</option>
              )}
              {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === status?.userId ? " (you)" : ""}</option>)}
            </select>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className={`wa-chip ${isMine ? "wa-chip--accent" : conversation.assignedTo ? "" : "wa-chip--warn"}`}>
                {isMine ? "You" : conversation.assignedToName || "Unassigned"}
              </span>
              {!conversation.assignedTo && (
                <button type="button" className="wa-btn wa-btn--sm wa-btn--primary" disabled={busy === "claim"} onClick={() => run("claim", WA_CLAIM, { id: conversation.id }, (d) => d.whatsappClaimConversation)}>Claim</button>
              )}
              {isMine && (
                <button type="button" className="wa-btn wa-btn--sm" disabled={busy === "assign"} onClick={() => run("assign", WA_ASSIGN, { id: conversation.id, userId: null }, (d) => d.whatsappAssignConversation)}>Release</button>
              )}
            </div>
          )}
        </div>
        <div className="wa-field" style={{ marginBottom: 0 }}>
          <label className="wa-label">Status</label>
          <div style={{ display: "flex", gap: 6 }}>
            {["open", "pending", "closed"].map((s) => (
              <button
                type="button"
                key={s}
                className={`wa-btn wa-btn--sm ${conversation.status === s ? "wa-btn--primary" : ""}`}
                disabled={!perms.canAct || busy === "status" || conversation.status === s}
                onClick={() => run("status", WA_SET_STATUS, { id: conversation.id, status: s }, (d) => d.whatsappSetConversationStatus)}
              >
                {WA_STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="wa-section">
        <div className="wa-section__title">
          Linked record
          {perms.canManage && (linked.lead || linked.client) && (
            <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" onClick={onLink}>Change</button>
          )}
        </div>
        {linked.lead && (
          <div className="wa-record">
            <div className="wa-record__kind">Lead{linked.lead.leadID ? ` #${linked.lead.leadID}` : ""}</div>
            <div className="wa-record__name">{linked.lead.agencyName || "Lead"}</div>
            <dl className="wa-record__grid">
              {linked.lead.contact && <><dt>Contact</dt><dd>{linked.lead.contact.name}{linked.lead.contact.title ? `, ${linked.lead.contact.title}` : ""}</dd></>}
              {linked.lead.contact?.email && <><dt>Email</dt><dd>{linked.lead.contact.email}</dd></>}
              {linked.lead.stage && <><dt>Stage</dt><dd>{linked.lead.stage}</dd></>}
              {linked.lead.assignedTo && <><dt>Owner</dt><dd>{linked.lead.assignedTo}</dd></>}
              {(linked.lead.city || linked.lead.state) && <><dt>Location</dt><dd>{[linked.lead.city, linked.lead.state].filter(Boolean).join(", ")}</dd></>}
              {linked.lead.timeZone && <><dt>Time zone</dt><dd>{linked.lead.timeZone}</dd></>}
            </dl>
            <div className="wa-record__actions">
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(openTab({ path: `/sales/${linked.lead.id}`, title: linked.lead.agencyName || "Lead" }))}>
                <i className="fa-solid fa-up-right-from-square" /> Open lead
              </button>
              {perms.canManage && (
                <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" disabled={busy === "unlink"} onClick={() => run("unlink", WA_UNLINK, { id: conversation.id, which: "lead" }, (d) => d.whatsappUnlinkConversation).then(() => dispatch(fetchWaConversation(conversation.id)))}>Unlink</button>
              )}
            </div>
          </div>
        )}
        {linked.client && (
          <div className="wa-record">
            <div className="wa-record__kind">Client</div>
            <div className="wa-record__name">{linked.client.clientName}</div>
            <dl className="wa-record__grid">
              {linked.client.email && <><dt>Email</dt><dd>{linked.client.email}</dd></>}
              {linked.client.status && <><dt>Status</dt><dd>{linked.client.status}</dd></>}
            </dl>
            <div className="wa-record__actions">
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(openTab({ path: `/clients/${linked.client.id}`, title: linked.client.clientName || "Client" }))}>
                <i className="fa-solid fa-up-right-from-square" /> Open client
              </button>
              {perms.canManage && (
                <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" disabled={busy === "unlink"} onClick={() => run("unlink", WA_UNLINK, { id: conversation.id, which: "client" }, (d) => d.whatsappUnlinkConversation).then(() => dispatch(fetchWaConversation(conversation.id)))}>Unlink</button>
              )}
            </div>
          </div>
        )}
        {!linked.lead && !linked.client && (
          <>
            {(detail.linkCandidates?.leads?.length || 0) + (detail.linkCandidates?.clients?.length || 0) > 1 && (
              <div className="wa-alert wa-alert--info" style={{ marginBottom: 8 }}>
                <i className="fa-solid fa-circle-info" /> This number matches several records — pick the right one.
              </div>
            )}
            {perms.canManage ? (
              <button type="button" className="wa-btn wa-btn--block" onClick={onLink}><i className="fa-solid fa-link" /> Link to record</button>
            ) : (
              <div className="wa-help">Not linked to a lead or client. A WhatsApp admin can link it.</div>
            )}
          </>
        )}
      </div>

      <div className="wa-section">
        <div className="wa-section__title">Tags</div>
        <div className="wa-tags">
          {(conversation.tags || []).map((t) => (
            <span className="wa-chip" key={t}>
              {t}
              {perms.canAct && <button type="button" onClick={() => removeTag(t)} aria-label={`Remove tag ${t}`}><i className="fa-solid fa-xmark" /></button>}
            </span>
          ))}
          {perms.canAct && (conversation.tags || []).length < MAX_TAGS && (
            <input
              value={tagInput}
              maxLength={32}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              onBlur={() => tagInput.trim() && addTag()}
              placeholder="+ Add tag"
              aria-label="Add tag"
              disabled={busy === "tags"}
            />
          )}
          {!perms.canAct && !(conversation.tags || []).length && <span className="wa-help">No tags</span>}
        </div>
      </div>

      <div className="wa-section">
        <div className="wa-section__title">WhatsApp consent</div>
        {consent.reachability?.status === "yes" && (
          <div className="wa-help" style={{ marginTop: 0 }}><i className="fa-solid fa-circle-check" style={{ color: "var(--wa-ok)" }} /> On WhatsApp (confirmed {fullTime(consent.reachability.at)})</div>
        )}
        {consent.reachability?.status === "no" && (
          <div className="wa-alert wa-alert--danger" style={{ marginBottom: 8 }}><i className="fa-solid fa-circle-xmark" /><div>Meta couldn't deliver to this number {consent.reachability.at ? `(${fullTime(consent.reachability.at)})` : ""} — it's likely not on WhatsApp.</div></div>
        )}
        {consent.optInStatus === "opted_in" && (
          <div className="wa-alert wa-alert--ok"><i className="fa-solid fa-user-check" /> Opted in {consent.optInAt ? fullTime(consent.optInAt) : ""}</div>
        )}
        {consent.optInStatus === "opted_out" && (
          <div className="wa-alert wa-alert--danger"><i className="fa-solid fa-ban" /> Opted out {consent.optOutAt ? fullTime(consent.optOutAt) : ""} — templates are blocked.</div>
        )}
        {(!consent.optInStatus || consent.optInStatus === "unknown") && (
          <div className="wa-help" style={{ marginTop: 0 }}>
            No explicit opt-in recorded.{conversation.lastInboundAt ? " They've messaged us, so utility templates are allowed; marketing templates need an opt-in." : ""}
          </div>
        )}
        {consent.optInStatus !== "opted_in" && (
          showConsent ? (
            <div style={{ marginTop: 8 }}>
              <ConsentForm compact phone={conversation.waId} leadId={conversation.leadId} onSaved={() => { setShowConsent(false); dispatch(fetchWaConversation(conversation.id)); }} />
            </div>
          ) : (
            <button type="button" className="wa-btn wa-btn--sm" style={{ marginTop: 8 }} onClick={() => setShowConsent(true)}><i className="fa-solid fa-user-check" /> Record opt-in</button>
          )
        )}
        {consent.optInStatus === "opted_in" && (
          <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" style={{ marginTop: 8 }} disabled={busy === "consent"} onClick={optOut}>Mark as opted-out</button>
        )}
        {detail.emailEngaged && <div className="wa-help"><i className="fa-solid fa-envelope-open" /> Engaged with our email — marketing templates allowed if opted in.</div>}
      </div>

      <div className="wa-section">
        <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost" onClick={loadAudit} aria-expanded={showAudit}>
          <i className={`fa-solid fa-chevron-${showAudit ? "down" : "right"}`} /> Activity log
        </button>
        {showAudit && (
          <div className="wa-audit" style={{ marginTop: 8 }}>
            {!audit && <div className="wa-help">Loading…</div>}
            {audit && !audit.length && <div className="wa-help">No activity yet.</div>}
            {audit && audit.length > 0 && (
              <ul>
                {audit.map((a) => (
                  <li key={a.id}>{a.summary}<time>{a.actorName ? `${a.actorName} · ` : ""}{fullTime(a.createdAt)}</time></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default ContextPanel;
